import { useEffect, useRef, useState } from 'react'
import { decodeToken } from '../../lib/auth'
import { useAuthSession } from '../../lib/authSession'
import { errorText } from '../recordEdits/api'
import type { ObjectKind } from '../recordEdits/contract'
import RemovalRequestPane from './RemovalRequestPane'
import { removeDirect, submitRemoval } from './api'
import { isRemovalRequest, removalStatus, type RemovalRequest } from './contract'
import './removals.css'

interface Props {
  target: { type: ObjectKind; id: string }
  fundId: string
  targetLabel?: string
  existingRequestIds?: readonly string[]
  onRemoved: () => void
  onRequested?: () => void
}

/** One role-aware form for the actual typed drawer and legacy object rows. */
export default function RemovalForm(props: Props) {
  const token = useAuthSession()
  const role = token ? decodeToken(token)?.role : null
  return (
    <RemovalFormIntent
      key={`${props.target.type}:${props.target.id}:${props.fundId}:${token ?? ''}`}
      {...props}
      role={role}
    />
  )
}

function RemovalFormIntent({
  target,
  fundId,
  targetLabel,
  existingRequestIds = [],
  onRemoved,
  onRequested,
  role,
}: Props & { role: string | null | undefined }) {
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [request, setRequest] = useState<RemovalRequest | null>(null)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [existingReview, setExistingReview] = useState<string | null>(null)
  const existingIds = [...new Set(existingRequestIds)]
  const inFlight = useRef(false)
  const mounted = useRef(true)
  const intent = useRef(crypto.randomUUID())
  const manager = role === 'manager' || role === 'admin'
  const allowed = manager || role === 'ops_officer'
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const submit = async () => {
    if (
      !allowed ||
      (!manager && existingIds.length > 0) ||
      !why.trim() ||
      inFlight.current ||
      request
    )
      return
    inFlight.current = true
    setBusy(true)
    setError(null)
    try {
      const targetType = target.type
      const result = manager
        ? await removeDirect(targetType, target.id, fundId, why.trim())
        : await submitRemoval({
            target_type: targetType,
            target_id: target.id,
            fund_id: fundId,
            reason: why.trim(),
            idempotency_key: intent.current,
          })
      if (!mounted.current) return
      if (isRemovalRequest(result)) {
        if (
          result.target_id !== target.id ||
          result.target_type !== targetType ||
          result.requested_fund_id !== fundId
        )
          throw new Error('The removal response belongs to another object or fund.')
        setRequest(result)
        onRequested?.()
      } else {
        if (
          result.id !== target.id ||
          result.target_type !== targetType ||
          result.fund_id !== fundId ||
          !result.deleted_at
        )
          throw new Error('Removal has not been confirmed. Refresh the Record.')
        onRemoved()
      }
    } catch (failure) {
      if (mounted.current) setError(errorText(failure))
    } finally {
      inFlight.current = false
      if (mounted.current) setBusy(false)
    }
  }

  return (
    <div className="removal-form" data-testid="record-removal-form">
      {!request && existingIds.length > 0 ? (
        <div data-testid="existing-removal-requests">
          <p role="status">
            Removal already requested. The Record stays unchanged until a manager approves.
          </p>
          {existingIds.map((id, index) => (
            <button
              key={id}
              type="button"
              className="vbtn"
              aria-expanded={existingReview === id}
              onClick={() => setExistingReview(existingReview === id ? null : id)}
            >
              {existingReview === id ? 'Close request' : 'Review removal request'}
              {existingIds.length > 1 ? ` ${index + 1}` : ''}
            </button>
          ))}
          {existingReview && existingIds.includes(existingReview) ? (
            <RemovalRequestPane
              requestId={existingReview}
              targetLabel={targetLabel}
              onChanged={(updated) => {
                if (updated.status === 'approved') onRemoved()
                else onRequested?.()
              }}
            />
          ) : null}
        </div>
      ) : null}
      {request ? (
        <>
          <p role="status">
            <strong>{removalStatus(request.status)}</strong> · {request.message}
          </p>
          <button type="button" className="vbtn" onClick={() => setReviewOpen(!reviewOpen)}>
            {reviewOpen ? 'Close removal review' : 'Review removal request'}
          </button>
          {reviewOpen ? (
            <RemovalRequestPane
              requestId={request.request_id}
              targetLabel={targetLabel ?? target.type}
              onChanged={(updated) => {
                setRequest(updated)
                if (updated.status === 'approved') onRemoved()
                else onRequested?.()
              }}
            />
          ) : null}
        </>
      ) : !manager && existingIds.length > 0 ? null : (
        <>
          <p style={{ margin: 0, fontSize: 'var(--text-12)', color: 'var(--muted)' }}>
            {manager
              ? `Remove this ${target.type} from the Record. Prior values remain in History.`
              : `Request removal of this ${target.type}. The Record stays unchanged until a manager approves.`}
          </p>
          <input
            value={why}
            onChange={(event) => {
              setWhy(event.target.value)
              intent.current = crypto.randomUUID()
            }}
            maxLength={2000}
            placeholder="Reason for removal (required)"
            aria-label="Reason for removal"
            disabled={busy || !allowed}
            className="removal-form__reason"
          />
          <div>
            <button
              type="button"
              className="vbtn removal-form__submit"
              onClick={() => void submit()}
              disabled={!why.trim() || busy || !allowed}
            >
              {busy
                ? manager
                  ? 'Removing…'
                  : 'Submitting…'
                : manager
                  ? `Remove this ${target.type}`
                  : 'Request removal'}
            </button>
          </div>
        </>
      )}
      {error ? (
        <p
          role="alert"
          style={{ fontSize: 'var(--text-12)', color: 'var(--gap)', whiteSpace: 'normal' }}
        >
          {error}
        </p>
      ) : null}
    </div>
  )
}
